/**
 * Rep-facing "Movers to review" panel.
 *
 * Surfaces the rep's material growers/decliners — buckets whose calibrated
 * baseline is moving materially year-over-year — so a plan-moving change is
 * eyeballed before the plan is committed (the HH-Associates failure class,
 * doc 12). It reuses the calibration list endpoint scoped to the rep's
 * `forecastId` with `trend=growing|declining`, so there is no second data
 * path and no client-side filtering. Each row deep-links into the bucket's
 * plan drill (`/plan/:repId/:bucketId`), where the Calibration card holds
 * the detail the old /calibration workspace used to show.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { BucketCalibrationRow } from "@shared-core/data/adapter";
import { fmtK } from "@shared-core/domain/format-money";
import { paths } from "@shared-core/routing/routes";
import { Card } from "../../primitives/card";

const MOVERS_PAGE_SIZE = 50;

export function MoversToReviewPanel({
  forecastId,
  repId,
}: {
  forecastId: string;
  repId: string;
}) {
  const adapter = useDataAdapter();
  const [growers, setGrowers] = useState<BucketCalibrationRow[]>([]);
  const [decliners, setDecliners] = useState<BucketCalibrationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // Two scoped calls to the same list endpoint — biggest movers first.
        const [growing, declining] = await Promise.all([
          adapter.listBucketCalibrations({
            forecastId,
            trend: "growing",
            sort: "-growth_pct",
            pageSize: MOVERS_PAGE_SIZE,
          }),
          adapter.listBucketCalibrations({
            forecastId,
            trend: "declining",
            sort: "growth_pct",
            pageSize: MOVERS_PAGE_SIZE,
          }),
        ]);
        if (cancelled) return;
        setGrowers(growing.items);
        setDecliners(declining.items);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, forecastId]);

  const hasMovers = growers.length > 0 || decliners.length > 0;

  return (
    <Card className="p-4 space-y-3">
      <div>
        <h3 className="text-base font-semibold">Movers to review</h3>
        <p className="text-xs text-muted-foreground">
          Buckets whose baseline is moving materially year-over-year. Eyeball
          each before committing the plan.
        </p>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground">Loading movers…</p>
      )}

      {error && (
        <p className="text-sm text-red-700">Failed to load movers: {error}</p>
      )}

      {!loading && !error && !hasMovers && (
        <p className="text-sm text-muted-foreground">
          No material movers — every bucket's baseline is holding steady.
        </p>
      )}

      {!loading && !error && hasMovers && (
        <div className="grid gap-4 sm:grid-cols-2">
          <MoverColumn title="Growing" tone="up" rows={growers} repId={repId} />
          <MoverColumn
            title="Declining"
            tone="down"
            rows={decliners}
            repId={repId}
          />
        </div>
      )}
    </Card>
  );
}

function MoverColumn({
  title,
  tone,
  rows,
  repId,
}: {
  title: string;
  tone: "up" | "down";
  rows: BucketCalibrationRow[];
  repId: string;
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">
        {title}
      </h4>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">None.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={`${r.forecastId}:${r.bucketId}`}>
              <Link
                to={paths.planBucket({ repId, bucketId: r.bucketId })}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5 text-sm hover:bg-surface-hover"
              >
                <span className="truncate">{r.bucketName}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-muted-foreground">
                    {fmtK(r.annualGbp)}
                  </span>
                  <span
                    className={
                      tone === "up" ? "text-green-700" : "text-red-700"
                    }
                  >
                    {tone === "up" ? "▲" : "▼"}
                    {r.growthPct == null
                      ? ""
                      : `${r.growthPct > 0 ? "+" : ""}${r.growthPct.toFixed(0)}%`}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
